import React from "react"
import { ChevronDown } from "styled-icons/feather"

import { categoryLabel, CF_CATEGORIES, CfCategory, ChannelSelection, summarizeCategories } from "../categories"
import * as styles from "../styles"

type ChannelCategoryPickerProps = {
  // Absent while the channel is not selected: no summary, no drawer, just the row.
  selection?: ChannelSelection
  isOpen: boolean
  onToggleOpen: () => void
  onToggleCategory: (category: CfCategory) => void
  disabled?: boolean
  testIdPrefix: string
  // The creation modal draws each channel as a bordered card; the batch menu as a plain row.
  variant: "menu" | "card"
  children: React.ReactNode
}

const ChannelCategoryPicker: React.FC<ChannelCategoryPickerProps> = ({
  selection, isOpen, onToggleOpen, onToggleCategory, disabled, testIdPrefix, variant, children,
}) => {
  const inCard = variant === "card"

  const summary = selection && (
    <styles.CategorySummaryButton
      type="button"
      aria-expanded={isOpen}
      disabled={disabled}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation()
        onToggleOpen()
      }}
      data-testid={`channel-category-summary-${testIdPrefix}`}
    >
      {summarizeCategories(selection.allowedCategories)}
      <ChevronDown size={11} color="currentColor" />
    </styles.CategorySummaryButton>
  )

  const drawer = selection && isOpen && (
    <styles.CategoryDrawer $inCard={inCard}>
      <styles.CategoryGrid>
        {CF_CATEGORIES.map((category) => {
          const isAllowed = selection.allowedCategories.includes(category)
          return (
            <styles.CategoryPill
              key={category}
              type="button"
              $active={isAllowed}
              aria-pressed={isAllowed}
              disabled={disabled}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                onToggleCategory(category)
              }}
              data-testid={`category-pill-${testIdPrefix}-${category}`}
            >
              {categoryLabel(category)}
            </styles.CategoryPill>
          )
        })}
      </styles.CategoryGrid>
    </styles.CategoryDrawer>
  )

  // The summary sits beside the channel inside the row; the drawer opens beneath it. Each variant
  // names its own frame, so neither set of transient props has to be widened to fit the other.
  if (inCard) {
    return (
      <styles.CreationChannelCard $isSelected={selection !== undefined} data-testid={`channel-card-${testIdPrefix}`}>
        <styles.CreationChannelRow>
          {children}
          {summary}
        </styles.CreationChannelRow>
        {drawer}
      </styles.CreationChannelCard>
    )
  }

  return (
    <styles.ChannelMenuRow $open={isOpen} data-testid={`channel-card-${testIdPrefix}`}>
      <styles.ChannelMenuRowTop>
        {children}
        {summary}
      </styles.ChannelMenuRowTop>
      {drawer}
    </styles.ChannelMenuRow>
  )
}

export default ChannelCategoryPicker
